// Workflow Builder. Config properties do not come from the element instances
// (i.e. they're not Parameters)  but are instead standard across all
// workflows. For historical reasons, they're hard-coded.  I'm somewhat
// confident that these properties should live somewhere in a Go struct
// definition and be handed down to the client so that there is a single source
// of truth. We're a long way off from that though.

import {
  ArrayAdditionalProps,
  DropdownAdditionalProps,
} from 'common/elementConfiguration/AdditionalEditorProps';
import { EditorType } from 'common/elementConfiguration/EditorType';
import { Markdown } from 'common/lib/markdown';
import { Parameter } from 'common/types/bundle';

/** Editor configuration to enable full configuration of a device */
export const CONFIGUREDDEVICESELECTION: Parameter = {
  description: '',
  name: 'configuredDeviceSelection', // arbitrary name; doesn't not correspond to a type or field
  type: '',
  configuration: {
    displayName: 'Execution Mode',
    displayDescription: '' as Markdown,
    editor: {
      type: EditorType.DEVICE_CONFIGURATION,
      placeholder: '',
    },
    isVisible: true,
  },
};

/** Editor configuration to enable full just selection of devices */
export const DEVICESELECTIONS: Parameter = {
  description: '',
  name: 'deviceSelections', // arbitrary name; doesn't not correspond to a type or field
  type: '',
  configuration: {
    displayName: 'Mode',
    displayDescription: '' as Markdown,
    editor: {
      type: EditorType.DEVICE_SELECTION,
      placeholder: '',
    },
    isVisible: true,
  },
};

/** Editor configuration to enable just selection of device's run configuration */
export const DECKSELECTION: Parameter = {
  description: '',
  name: 'deckSelection', // arbitrary name; doesn't not correspond to a type or field
  type: '',
  configuration: {
    displayName: 'Deck Layout',
    displayDescription: '' as Markdown,
    editor: {
      type: EditorType.DECK_OPTIONS,
      placeholder: '',
    },
    isVisible: true,
  },
};

export const INPUTPLATES: Parameter = {
  description:
    'Depending on your workflow, Synthace might automatically define the input plates needed. ' +
    'Here you can specify the types of plates you would like to use.',
  name: 'inputPlateTypes', // corresponds to ConfiguredDevice field
  type: '',
  configuration: {
    displayName: 'Input Plate Types',
    displayDescription: '' as Markdown,
    editor: {
      type: EditorType.ARRAY,
      placeholder: '',
      additionalProps: {
        overrideAddNewItemCopy: 'New plate type',
        itemEditor: {
          type: EditorType.PLATE_TYPE,
          additionalProps: {
            editor: EditorType.PLATE_TYPE,
            plateMode: 'input',
          },
        },
      } as ArrayAdditionalProps,
    },
    isVisible: true,
  },
};

export const TIPTYPES: Parameter = {
  description: 'Types of tips you would like to use.',
  name: 'tipTypes', // corresponds to ConfiguredDevice field
  type: '',
  configuration: {
    displayName: 'Tip Types',
    displayDescription: '' as Markdown,
    editor: {
      type: EditorType.TIP_TYPE,
      placeholder: '',
    },
    isVisible: true,
  },
};

// name fields corresponds to GlobalMixerConfig fields
export const GLOBALMIXERCONFIG: Parameter[] = [
  {
    description:
      'Ignore the output of physical simulation if a workflow is known to be safe.',
    name: 'ignorePhysicalSimulation',
    type: 'bool',
    configuration: {
      displayName: 'Ignore Physical Simulation',
      displayDescription: '' as Markdown,
      editor: {
        type: EditorType.TOGGLE,
        placeholder: '',
      },
      isVisible: true,
    },
  },
  {
    description:
      'If enabled, at most one tip box for each tip type needed will be placed on the deck. If ' +
      'more tip boxes are needed the liquid handler will stop and prompt the user to change the ' +
      'tip box.',
    name: 'useDriverTipTracking',
    type: 'bool',
    configuration: {
      displayName: 'One Box Per Tip Type',
      displayDescription: '' as Markdown,
      editor: {
        type: EditorType.TOGGLE,
        placeholder: '',
      },
      isVisible: true,
    },
  },
  {
    description:
      'If enabled, Synthace will try to fill all empty deck positions with relevant tip boxes, thus minimizing ' +
      'the number of manual tip box changes. If disabled, Synthace will only add one tip box for each type of used tips. ' +
      'It can only be enabled if useDriverTipTracking is enabled.',
    name: 'useTipboxAutofill',
    type: 'bool',
    configuration: {
      displayName: 'Allocate Extra Tip Boxes',
      displayDescription: '' as Markdown,
      editor: {
        type: EditorType.TOGGLE,
        placeholder: '',
      },
      isVisible: true,
    },
  },
  {
    description:
      'Let the vendor software track the state of tipboxes on deck and choose the locations where to load tips from on its own.',
    name: 'flexibleTipLoading',
    type: 'bool',
    configuration: {
      displayName: 'Flexible Tip Loading',
      displayDescription: '' as Markdown,
      editor: {
        type: EditorType.TOGGLE,
        placeholder: '',
      },
      isVisible: true,
    },
  },
  {
    description:
      'Check this box if you want Synthace to use more tips in order to increase the speed of execution.',
    name: 'useMoreTipsForSpeed',
    type: 'bool',
    configuration: {
      displayName: 'Use More Tips for Speed',
      displayDescription: '' as Markdown,
      editor: {
        type: EditorType.TOGGLE,
        placeholder: '',
      },
      isVisible: true,
    },
  },
  {
    description:
      'For workflows using centrifugation, specifies how balance plates should be created',
    name: 'balancingStrategy',
    type: 'string',
    configuration: {
      displayName: 'Centrifuge Balancing Strategy',
      displayDescription: '' as Markdown,
      editor: {
        type: EditorType.DROPDOWN,
        placeholder: '',
        additionalProps: {
          editor: EditorType.DROPDOWN,
          options: [
            { value: 'empty', label: 'Empty Plate' },
            { value: 'manual volume', label: 'Manual Volume' },
            { value: 'automated volume', label: 'Automated Volume' },
          ],
        } as DropdownAdditionalProps,
      },
      isVisible: true,
    },
  },
  {
    description:
      'The maximum imbalance that can tolerated by the centrifuge when using automated or manual volume balancing',
    name: 'balancingTolerance',
    type: 'float64',
    configuration: {
      displayName: 'Centrifuge Balancing Tolerance (ml)',
      displayDescription: '' as Markdown,
      editor: {
        type: EditorType.FLOAT,
        placeholder: '',
      },
      isVisible: true,
    },
  },
  {
    description: 'Enable to allow Synthace to control your centrifuge',
    name: 'isCentrifugeEnabled',
    type: 'bool',
    configuration: {
      displayName: 'Enable Automated Centrifugation',
      displayDescription: '' as Markdown,
      editor: {
        type: EditorType.TOGGLE,
        placeholder: '',
      },
      isVisible: true,
    },
  },
  {
    description:
      'When Liquid Policies are applied use a new algorithm to ensure that User policies are a higher priority. Specifically for resolving LLD/LLF/Reference per transfer ensuring that system rules are a Secondary concern to the user policies.',
    name: 'improvedWellReferences',
    type: 'bool',
    configuration: {
      displayName: 'Improved Well References',
      displayDescription: '' as Markdown,
      editor: {
        type: EditorType.TOGGLE,
        placeholder: '',
      },
      isVisible: true,
    },
  },
  {
    description:
      'Check this box if you want Synthace to ensure all calculated transfer volumes are strictly a multiple of the discrete transfer volume step size the device is capable of. If unchecked, volumes will be rounded to the nearest step size at execution, which mean final concentrations in the platform are not strictly accurate.',
    name: 'isStrictDiscreteTransfers',
    type: 'bool',
    configuration: {
      displayName: 'Ensure Discrete Transfer Volumes',
      displayDescription: '' as Markdown,
      editor: {
        type: EditorType.TOGGLE,
        placeholder: '',
      },
      isVisible: true,
    },
  },
];

const GLOBALMIXERCONFIG_LEGACY_FIELDS: Parameter[] = [
  {
    description: 'List of deck positions. Advanced setting.',
    name: 'driverSpecificInputPreferences',
    type: '[]string',
  },
  {
    description: 'List of deck positions. Advanced setting.',
    name: 'driverSpecificOutputPreferences',
    type: '[]string',
  },
  {
    description:
      'List of temporary deck positions to use for moving plates from Hotels. Advanced setting.',
    name: 'driverSpecificTemporaryLocations',
    type: '[]string',
  },
  {
    description:
      'Override the location preferences for specific plates whose names match the prefixes given by the keys of the map. ' +
      'Matching is case sensitive. If a plate name matches two keys then the longest prefix is selected. ' +
      'For example given: ' +
      '  {"auto": ["position_1"], "auto_input": ["position_2"]} ' +
      'then a plate called "auto_input_plate_1" will be assigned to "position_2", ' +
      'while a plate called "automatic" will be assigned to "position_1"',
    name: 'driverSpecificPlatePreferences',
    type: 'map[string][]string',
  },
  {
    description:
      'Version of the input auto-allocation algorithm to use during simulation.',
    name: 'allocateInputsVersion',
    type: 'int',
  },
  {
    description: 'List of deck positions. Advanced setting.',
    name: 'driverSpecificTipPreferences',
    type: '[]string',
  },
  {
    description: 'List of deck positions. Advanced setting.',
    name: 'driverSpecificTipWastePreferences',
    type: '[]string',
  },
];

export const ALL_CONFIG_PROPERTIES: Parameter[] = [
  INPUTPLATES,
  TIPTYPES,
  ...GLOBALMIXERCONFIG,
  ...GLOBALMIXERCONFIG_LEGACY_FIELDS,
];

// For v1, we only need tipTypes and driverTipTracking.
export const CHERRYPICK_CONFIG = [
  TIPTYPES,
  {
    description:
      'If enabled, at most one tip box for each tip type needed will be placed on the deck. If ' +
      'more tip boxes are needed the liquid handler will stop and prompt the user to change the ' +
      'tip box.',
    name: 'useDriverTipTracking',
    type: 'bool',
  },
];
